import React, { ReactNode, useState } from 'react';

import { DateCalendar, DateCalendarProps } from '@mui/x-date-pickers/DateCalendar';
import ArrowButton from 'components/ArrowButton';
import MediaTypography from 'components/MediaTypography';
import { DateTime } from 'luxon';

import {
	StyledDatePickerCalendarMonth,
	StyledDatePickerPopover,
	StyledDatePickerWrap,
} from './styled';

export interface DatePickerProps extends DateCalendarProps<any> {
	value: DateTime;
	onChange: (value: any) => void;
	format?: 'day' | 'week' | 'month';
	children?: ReactNode;
	daysNumber?: number;
	formatDate?: (date: DateTime) => string;
}

const getWeekRange = (date: DateTime, daysNumber: number) => {
	const startOfWeek = date.startOf('week').plus({ days: 0 }); // Monday
	const endOfWeek = date.startOf('week').plus({ days: daysNumber - 1 }); // Friday by default
	const formattedStart = startOfWeek.toFormat('dd.MM.yyyy');
	const formattedEnd = endOfWeek.toFormat('dd.MM.yyyy');

	return `${formattedStart} - ${formattedEnd}`;
};

function DatePicker({
	value,
	onChange,
	format = 'day',
	children,
	daysNumber = 1,
	formatDate,
	...props
}: DatePickerProps) {
	const [anchorEl, setAnchorEl] = useState(null);
	const handleClose = () => {
		setAnchorEl(false);
	};

	const handleClick = (event: React.MouseEvent<SVGSVGElement>) => {
		setAnchorEl(event.currentTarget);
	};

	const handlePrevDate = () => {
		const newValue = value.minus({ [format]: 1 });
		onChange(newValue);
	};

	const handleNextDate = () => {
		const newValue = value.plus({ [format]: 1 });
		onChange(newValue);
	};

	const isOpen = Boolean(anchorEl);
	const isWeek = format === 'week';

	// eslint-disable-next-line no-nested-ternary
	const date = formatDate
		? formatDate(value)
		: isWeek
			? getWeekRange(value, daysNumber)
			: value.toFormat('ccc, dd.MM.yyyy');

	return (
		<>
			<StyledDatePickerWrap className="datePicker">
				<ArrowButton direction="left" onClick={handlePrevDate} />
				<MediaTypography variant="subtitle2">{date}</MediaTypography>
				<StyledDatePickerCalendarMonth onClick={handleClick} />
				<ArrowButton direction="right" onClick={handleNextDate} />
			</StyledDatePickerWrap>
			<StyledDatePickerPopover open={isOpen} onClose={handleClose} anchorEl={anchorEl}>
				{!!children && children}
				<DateCalendar
					value={value}
					onChange={onChange}
					dayOfWeekFormatter={(date: DateTime) => date.toFormat('ccc').toUpperCase()}
					{...props}
				/>
			</StyledDatePickerPopover>
		</>
	);
}

export default DatePicker;
